import { useCallback, useState, useMemo, useRef } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { usePepwiseAuthController } from 'Context/contexts/AuthContext';
import bulkUploadIcon from 'assets/hugeicons_file-upload.png';
import MetadataUpdates from './MetadataUpdates';
import PageWrapper from 'components/Common/PageWraper';
import QueryBuilderPanel, { QueryBuilderFieldOption, QueryBuilderFilter } from 'components/Common/QueryBuilderPanel';
import CustomizedTable from 'components/CustomizedTable/CustomizedTable';
import { checkRole } from 'utils/roleUtils';
import { fetchJobMetadataExport, fetchJobMetadataFilterOptions, fetchJobMetadataTableData } from 'service/service';
import CaptureExportControls from 'utils/CaptureExportControls';
import { ExportIcon } from 'components/CommonIcons/Icons';

const DEFAULT_COLUMN_ORDER = [
  'env',
  'platform_name',
  'scheduler_name',
  'workspace_name',
  'app_name',
  'schedtab',
  'job_id',
  'job_name',
  'sector',
  'program',
  'domain',
  'gdo',
  'source_category',
  'source_subcategory',
  'orchestrator',
  'business_unit',
  'department',
  'capability',
  'technology',
  'dataasset_name',
  'bronze_table',
  'silver_table',
  'gold_table',
  'sustain_owner',
  'sustain_prod_date',
  'layer',
  'market',
  'market_group',
  'business_stream',
  'business_application',
];

const FILTER_FIELD_API_KEY_OVERRIDES: Record<string, string> = {
  business_unit: 'businessUnit',
};

const toApiFieldKey = (field: string) => FILTER_FIELD_API_KEY_OVERRIDES[field] || field;

const formatFieldLabel = (key: string) =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[_\s]+/)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');

const metadataFilterFieldOptions: QueryBuilderFieldOption[] = DEFAULT_COLUMN_ORDER.map((key) => ({
  label: formatFieldLabel(key),
  value: key,
}));

const buildFiltersPayload = (activeFilters: QueryBuilderFilter[]): Record<string, string[]> => {
  const payload: Record<string, string[]> = {};
  activeFilters.forEach((filter) => {
    if (!filter.field || !filter.values?.length) {
      return;
    }
    payload[toApiFieldKey(filter.field)] = filter.values;
  });
  return payload;
};

type SearchAfterCursor = Array<string | number>;

const MetadataUpdatesPage = () => {
  const [controller] = usePepwiseAuthController();
  const { role } = controller;
  const isAllowed = checkRole(role, ['Data Operations Admin', 'PepWise System User']);
  const [isMetadataDialogOpen, setIsMetadataDialogOpen] = useState(false);
  const [showQueryBuilderView, setShowQueryBuilderView] = useState(true);
  const [filters, setFilters] = useState<QueryBuilderFilter[]>([
    { id: 1, field: metadataFilterFieldOptions[0]?.value ?? '', values: [] },
  ]);
  const [appliedFilters, setAppliedFilters] = useState<QueryBuilderFilter[]>([]);
  const [hasRunQuery, setHasRunQuery] = useState(false);

  const [tableRows, setTableRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const cursorHistoryRef = useRef<Record<number, SearchAfterCursor | null>>({ 1: null, });
  const resetCursorHistory = () => {
    cursorHistoryRef.current = {
      1: null,
    };
  };
  const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: '' });
  const [isQuerying, setIsQuerying] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const INTERNAL_METADATA_COLUMNS = new Set(['to_be_deleted', '_sort', 'pipelineName',]);
  const COLUMN_SOURCE_KEY_OVERRIDES: Record<string, string> = {
    job_id: 'jobId',
    business_unit: 'businessUnit',
  };

  const resolveColumnSourceKey = (fieldKey: string) => COLUMN_SOURCE_KEY_OVERRIDES[fieldKey] ?? fieldKey;

  const formatCellValue = (value: unknown) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const buildColumnDefinitions = (fields: Array<{ key: string; label?: string; type?: string }>) => {
    const ordered = DEFAULT_COLUMN_ORDER.flatMap((fieldKey) => {
      const matchingField = fields.find(
        (opt) => opt.key === fieldKey || resolveColumnSourceKey(fieldKey) === opt.key
      );

      if (!matchingField) {
        return [];
      }

      return [
        {
          key: fieldKey,
          label: formatFieldLabel(fieldKey),
          sourceKey: resolveColumnSourceKey(fieldKey),
          type: matchingField.type ?? 'text',
        },
      ];
    });

    const orderedSourceKeys = new Set(ordered.map((field) => field.sourceKey));

    const additional = fields
      .filter((field) => {
        const fieldSourceKey = resolveColumnSourceKey(field.key);
        return (
          !INTERNAL_METADATA_COLUMNS.has(field.key) &&
          !DEFAULT_COLUMN_ORDER.includes(field.key) &&
          !DEFAULT_COLUMN_ORDER.includes(fieldSourceKey) &&
          !orderedSourceKeys.has(field.key) &&
          !orderedSourceKeys.has(fieldSourceKey)
        );
      })
      .map((field) => ({
        key: field.key,
        label: field.label ? formatFieldLabel(field.label) : formatFieldLabel(field.key),
        sourceKey: field.key,
        type: field.type ?? 'text',
      }));

    return [...ordered, ...additional];
  };

  const EXPORT_COLUMN_LABELS: Record<string, string> = {
    env: 'ENV',
    platform_name: 'PLATFORM NAME',
    scheduler_name: 'SCHEDULER NAME',
    workspace_name: 'WORKSPACE NAME',
    app_name: 'APP NAME',
    schedtab: 'SCHEDTAB',
    job_id: 'JOB ID',
    job_name: 'JOB NAME',
    sector: 'SECTOR',
    program: 'PROGRAM',
    business_stream: "BUSINESS STREAM",
    domain: 'DOMAIN',
    gdo: 'GDO',
    source_category: 'SOURCE CATEGORY',
    source_subcategory: 'SOURCE SUBCATEGORY',
    orchestrator: 'ORCHESTRATOR',
    businessUnit: 'BUSINESSUNIT',
    business_unit: 'BUSINESSUNIT',
    department: 'DEPARTMENT',
    capability: 'CAPABILITY',
    technology: 'TECHNOLOGY',
    dataasset_name: 'DATAASSET NAME',
    bronze_table: 'BRONZE TABLE',
    silver_table: 'SILVER TABLE',
    gold_table: 'GOLD TABLE',
    sustain_owner: 'SUSTAIN OWNER',
    sustain_prod_date: 'SUSTAIN PROD DATE',
    layer: 'LAYER',
    market: 'MARKET',
    market_group: 'MARKET GROUP',
    platform: 'PLATFORM',
    fileName: 'FILE NAME',
    eventSource: 'EVENT SOURCE',
    category: 'CATEGORY',
    isDeleted: 'IS DELETED',
    business_application: 'BUSINESS APPLICATION',
    timestamp: 'TIMESTAMP',
    metadataUUID: 'METADATA UUID',
    uuid: 'UUID',
  };
  const [availableFields, setAvailableFields] = useState(
    buildColumnDefinitions(DEFAULT_COLUMN_ORDER.map((key) => ({ key, label: key, type: 'text' })))
  );

  const tableColumns = useMemo(() => {
    return availableFields.map((field) => ({
      key: field.key,
      name: field.label ?? field.key,
      width: 200,
      minWidth: 150,
      formatter: ({ row }: any) => formatCellValue(row[field.sourceKey ?? field.key]),
    }));
  }, [availableFields]);

  const defaultColumns = useMemo(() => {
    return tableColumns
      .filter((col) => DEFAULT_COLUMN_ORDER.includes(col.key))
      .map((col) => col.name);
  }, [tableColumns]);

  const customizedTableKey = useMemo(() => tableColumns.map((col) => col.key).join('|'), [tableColumns]);

  const loadMetadataTable = useCallback(
    async (overrides?: {
      page?: number;
      pageSize?: number;
      sortingInfo?: { sortBy: string; sortDir: string };
      filtersToUse?: QueryBuilderFilter[];
      searchAfter?: SearchAfterCursor | null;
    }) => {
      const effectivePage = overrides?.page ?? page;
      const effectiveSearchAfter =
        overrides &&
        Object.prototype.hasOwnProperty.call(
          overrides,
          'searchAfter'
        )
          ? overrides.searchAfter ?? null
          : cursorHistoryRef.current[effectivePage] ?? null;
      const effectivePageSize = overrides?.pageSize ?? pageSize;
      const effectiveSorting = overrides?.sortingInfo ?? sortingInfo;
      const effectiveFilters = overrides?.filtersToUse ?? appliedFilters;

      setIsQuerying(true);
      try {
        const requestBody = {
          page: effectivePage,
          pageSize: effectivePageSize,
          sortBy: effectiveSorting.sortBy || 'timestamp',
          orderBy: effectiveSorting.sortDir ? effectiveSorting.sortDir.toUpperCase() : 'DESC',
          filters: buildFiltersPayload(effectiveFilters),
          searchAfter: effectiveSearchAfter,
        };

        const response = await fetchJobMetadataTableData(requestBody);
        const result = response?.data?.data ?? response?.data ?? response ?? {};

        const pagination = result.pagination ?? {};
        const responseNextCursor = Array.isArray(pagination.searchAfter)
          ? pagination.searchAfter
          : null;

        if (responseNextCursor) {
          cursorHistoryRef.current[
            effectivePage + 1
          ] = responseNextCursor;
        } else {
          delete cursorHistoryRef.current[
            effectivePage + 1
          ];
        }

        const rows = result.records ?? [];

        setTableRows(rows);
        setTotal(pagination?.totalElements ?? 0);

        const apiFieldDefinitions = Array.isArray(result.availableFields)
          ? result.availableFields.filter((field: any) => field?.key && !INTERNAL_METADATA_COLUMNS.has(field.key))
          : [];

        let nextAvailableFields;
        if (apiFieldDefinitions.length > 0) {
          nextAvailableFields = buildColumnDefinitions(apiFieldDefinitions);
        } else if (rows.length > 0) {
          nextAvailableFields = buildColumnDefinitions(
            Object.keys(rows[0])
              .filter((key) => !INTERNAL_METADATA_COLUMNS.has(key) && DEFAULT_COLUMN_ORDER.includes(key))
              .map((key) => ({ key, label: key, type: 'text' }))
          );
        } else {
          nextAvailableFields = buildColumnDefinitions(
            DEFAULT_COLUMN_ORDER.map((key) => ({ key, label: key, type: 'text' }))
          );
        }

        setAvailableFields(nextAvailableFields);
      } catch (error) {
        console.error('Failed to fetch metadata table data:', error);
        setTableRows([]);
        setTotal(0);
      } finally {
        setIsQuerying(false);
      }
    },
    [page, pageSize, sortingInfo, appliedFilters]
  );

  const handlePageChange = (value: number) => {
    setPage(value);
    if (hasRunQuery) {
      loadMetadataTable({ page: value });
    }
  };

  const handlePageSizeChange = (size: number) => {
    resetCursorHistory();
    setPageSize(size);
    setPage(1);
    if (hasRunQuery) {
      loadMetadataTable({ page: 1, pageSize: size, searchAfter: null, });
    }
  };

  const handleSortingChange = (sortInfo: { sortBy: string; sortDir: string }) => {
    resetCursorHistory();
    setSortingInfo(sortInfo);
    setPage(1);
    if (hasRunQuery) {
      loadMetadataTable({ page: 1, sortingInfo: sortInfo, searchAfter: null, });
    }
  };

  const handleRunQuery = (queryFilters: QueryBuilderFilter[]) => {
    resetCursorHistory();
    setAppliedFilters(queryFilters);
    setPage(1);
    setHasRunQuery(true);
    setShowQueryBuilderView(true);
    loadMetadataTable({ page: 1, filtersToUse: queryFilters, searchAfter: null, });
  };

  const handleFetchFieldValues = useCallback(async (field: string, searchTerm = '') => {
    try {
      const response = await fetchJobMetadataFilterOptions({
        field: toApiFieldKey(field),
        searchTerm,
        limit: 100,
      });
      const result = response?.data?.data ?? response?.data ?? response ?? {};
      return result.options ?? [];
    } catch (error) {
      console.error(`Failed to fetch values for field "${field}":`, error);
      return [];
    }
  }, []);

  const openMetadataDialog = () => setIsMetadataDialogOpen(true);

  const handleMetadataClose = (showQueryBuilder = false) => {
    setIsMetadataDialogOpen(false);
    if (showQueryBuilder) {
      resetCursorHistory();
      setShowQueryBuilderView(true);
      setHasRunQuery(true);
      setPage(1);
      loadMetadataTable({ page: 1, filtersToUse: appliedFilters, searchAfter: null, });
    }
  };

  const handleExportTableData = useCallback(
    async (preferredColumnNames: string[]) => {
      const selectedColumnNames = preferredColumnNames?.length ? preferredColumnNames : tableColumns.map((col) => col.name);
      const exportColumns = selectedColumnNames.map((columnName) => {
        const matchingColumn = tableColumns.find((col) => col.name === columnName);
        return matchingColumn ? EXPORT_COLUMN_LABELS[matchingColumn.key] || matchingColumn.name : columnName;
      });

      const requestBody = {
        sortBy: sortingInfo.sortBy || 'timestamp',
        orderBy: sortingInfo.sortDir ? sortingInfo.sortDir.toUpperCase() : 'DESC',
        filters: buildFiltersPayload(appliedFilters),
        exportColumns,
      };
      return fetchJobMetadataExport(requestBody);
    },
    [sortingInfo, appliedFilters, tableColumns]
  );


const handleExportClick = useCallback(async () => {
  if (isExporting) return;

  setIsExporting(true);
  try {
    const tableType = "metadata-updates"
    const storedPref: any = localStorage.getItem(`${tableType}_user_col_pref`);
    const storedColumnNames: any = storedPref ? JSON.parse(storedPref) : null;
    const preferredColumnNames = storedColumnNames && storedColumnNames.length ? storedColumnNames : defaultColumns;

    await handleExportTableData(preferredColumnNames);
  } catch (error) {
    console.error('Failed to export metadata updates:', error);
  } finally {
    setIsExporting(false);
  }
}, [handleExportTableData, defaultColumns, isExporting]);

  if (!isAllowed) {
    return null;
  }

  return (
    <PageWrapper>
      <Box sx={{ p: 2.5, pt: 1.5, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
            Metadata Updates
          </Typography>
          <Button
              variant="contained"
              onClick={openMetadataDialog}
              sx={{
                backgroundColor: 'rgba(0, 155, 216, 1)',
                color: '#ffffff',
                textTransform: 'none',
                borderRadius: '6px',
                boxShadow: 'none',
                px: 2.5,
                py: 0.8,
                fontSize: '0.8125rem',
                fontWeight: 700,
                minWidth: '140px',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                '&:hover': { backgroundColor: 'rgba(0,120,180,1)', boxShadow: 'none' },
              }}
            >
              <img src={bulkUploadIcon} alt="Upload" style={{ width: '18px', height: '18px' }} />{' '}
              BULK UPLOAD
            </Button>
        </Box>

        {showQueryBuilderView ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <QueryBuilderPanel
                  filters={filters}
                  onFiltersChange={setFilters}
                  filterFields={metadataFilterFieldOptions}
                  fetchFieldValues={handleFetchFieldValues}
                  onRunQuery={handleRunQuery}
                  runQueryButtonLabel="RUN QUERY"
                />
              </Box>
              <CaptureExportControls
                onScreenshot={() => undefined}
                screenCapturing={false}
                onExport={handleExportClick}
                isExporting={isExporting}
                isVisible={true}
                showExport={true}
                type="pipeline"
                exportIcon={<ExportIcon />}
                exportLabel='Export CSV'
                sx={{
                  flexDirection: 'row',
                  gap: 0,
                  ml: 0,
                  mb: 3,
                  alignSelf: 'flex-end',
                  zIndex: 2,
                  '& #exclude-camera': { display: 'none' },
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ backgroundColor: 'transparent' }}>
                {isQuerying ? (
                  <>
                  <Typography variant="h4" sx={{color: '#003771',fontWeight: 500, mb: 2,}}>
                    Details
                  </Typography>
                  <Box sx={{ height: 450, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                    <CircularProgress size={50} />
                    </Box>
                  </>
                  ) : (
                <CustomizedTable
                   key={customizedTableKey}
                   tableTitle="Details"
                   tableData={tableRows}
                   total={total}
                   page={page}
                   pageSize={pageSize}
                   onPageChange={handlePageChange}
                   onPageSizeChange={handlePageSizeChange}
                   isPrevNextPagination
                   onSorting={handleSortingChange}
                   sortingInfo={sortingInfo}
                   defaultColumns={defaultColumns}
                   tableType="metadata-updates"
                   columns={tableColumns}
                   hasGroupBy={false}
                   showExport={true}
                   fileName="Metadata_Updates"
                   exportColumns={tableColumns}
                   exportTableData={handleExportTableData}
                   noRowsFallback={hasRunQuery ? undefined : (
                     <Box
                       width="100%"
                       display="flex"
                       alignItems="center"
                       justifyContent="center"
                       textAlign="center"
                       className="records-msg"
                       sx={{
                         position: 'absolute',
                         top: 0,
                         left: 0,
                         right: 0,
                         bottom: 0,
                         color: '#000 !important',
                       }}
                       p={2}
                     >
                       Run Query to view results
                     </Box>
                   )}
                 />
                )}
              </Box>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              p: 4,
              backgroundColor: 'transparent',
              minHeight: 'calc(100vh - 400px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography color="textSecondary" variant="body1">No Metadata Available</Typography>
          </Box>
        )}
      </Box>

      <MetadataUpdates open={isMetadataDialogOpen} onCloseAction={handleMetadataClose} />
    </PageWrapper>
  );
};

export default MetadataUpdatesPage;
